import {
	Directive,
	ElementRef,
	Input,
	Renderer2,
	Signal,
	WritableSignal,
	computed,
	effect,
	input,
	signal
} from '@angular/core';
import { PlatformService } from 'lib/platform/index.js';

interface ITextDirective {
	className: string;
	enabled: Signal<boolean>;
}

/**
 * Platform service is used because if the class is added on server, it would have to be
 * removed from DOM when resolving within client component
 */
function addClass(
	platformService: PlatformService,
	renderer2: Renderer2,
	element: Element,
	className: string,
	prefixOfClassNameToRemove: string
): void {
	if (element) {
		const classes: string[] = element.className
			?.trim()
			.split(' ')
			.map((m) => m?.trim());

		for (const existingClass of classes) {
			if (existingClass.toLowerCase().startsWith(prefixOfClassNameToRemove.toLowerCase())) {
				renderer2.removeClass(element, existingClass);
			}
		}

		renderer2.addClass(element, className);
	}
}

/**
 * Platform service is used because if the class is added on server, it would have to be
 * removed from DOM when resolving within client component
 */
function removeClass(
	platformService: PlatformService,
	renderer2: Renderer2,
	element: Element,
	className: string
): void {
	if (element) {
		renderer2.removeClass(element, className);
	}
}

function addOrRemoveMainClasses(
	platformService: PlatformService,
	enabled: boolean | string,
	renderer2: Renderer2,
	hostElement: ElementRef,
	className: string
): void {
	if (!hostElement) {
		return;
	}

	if (enabled) {
		addClass(platformService, renderer2, hostElement.nativeElement, className, className);
	} else {
		removeClass(platformService, renderer2, hostElement.nativeElement, className);
	}
}

@Directive({
    selector: '[libText1]',
    standalone: false
})
export class Text1Directive implements ITextDirective {
	public readonly className = 'w-typography-text-1';

	public readonly enabled: Signal<boolean> = computed(() => {
		return this.libText1() === false ? false : true;
	});

	public readonly libText1 = input<boolean | ''>();

	constructor(
		private platformService: PlatformService,
		private renderer: Renderer2,
		private hostElement: ElementRef
	) {
		effect(() => {
			const enabled = this.enabled();
			addOrRemoveMainClasses(this.platformService, enabled, this.renderer, this.hostElement, this.className);
		});
	}
}

@Directive({
    selector: '[libText2]',
    standalone: false
})
export class Text2Directive implements ITextDirective {
	public readonly className = 'w-typography-text-2';

	public readonly enabled: Signal<boolean> = computed(() => {
		return this.libText2() === false ? false : true;
	});

	public readonly libText2 = input<boolean | ''>();

	constructor(
		private platformService: PlatformService,
		private renderer: Renderer2,
		private hostElement: ElementRef
	) {
		effect(() => {
			const enabled = this.enabled();
			addOrRemoveMainClasses(this.platformService, enabled, this.renderer, this.hostElement, this.className);
		});
	}
}

@Directive({
    selector: '[libText3]',
    standalone: false
})
export class Text3Directive implements ITextDirective {
	public readonly className = 'w-typography-text-3';

	public readonly enabled: Signal<boolean> = computed(() => {
		return this.libText3() === false ? false : true;
	});

	public readonly libText3 = input<boolean | ''>();

	constructor(
		private platformService: PlatformService,
		private renderer: Renderer2,
		private hostElement: ElementRef
	) {
		effect(() => {
			const enabled = this.enabled();
			addOrRemoveMainClasses(this.platformService, enabled, this.renderer, this.hostElement, this.className);
		});
	}
}

@Directive({
    selector: '[libText4]',
    standalone: false
})
export class Text4Directive implements ITextDirective {
	public readonly className = 'w-typography-text-4';

	public readonly enabled: Signal<boolean> = computed(() => {
		return this.libText4() === false ? false : true;
	});

	public readonly libText4 = input<boolean | ''>();

	constructor(
		private platformService: PlatformService,
		private renderer: Renderer2,
		private hostElement: ElementRef
	) {
		effect(() => {
			const enabled = this.enabled();
			addOrRemoveMainClasses(this.platformService, enabled, this.renderer, this.hostElement, this.className);
		});
	}
}

@Directive({
    selector: '[libText5]',
    standalone: false
})
export class Text5Directive implements ITextDirective {
	public readonly className = 'w-typography-text-5';

	public readonly enabled: Signal<boolean> = computed(() => {
		return this.libText5() === false ? false : true;
	});

	public readonly libText5 = input<boolean | ''>();

	constructor(
		private platformService: PlatformService,
		private renderer: Renderer2,
		private hostElement: ElementRef
	) {
		effect(() => {
			const enabled = this.enabled();
			addOrRemoveMainClasses(this.platformService, enabled, this.renderer, this.hostElement, this.className);
		});
	}
}

@Directive({
    selector: '[libLightText1]',
    standalone: false
})
export class TextLight1Directive implements ITextDirective {
	public readonly className = 'w-typography-light-text-1';

	public readonly enabled: Signal<boolean> = computed(() => {
		return this.libLightText1() === false ? false : true;
	});

	public readonly libLightText1 = input<boolean | ''>();

	constructor(
		private platformService: PlatformService,
		private renderer: Renderer2,
		private hostElement: ElementRef
	) {
		effect(() => {
			const enabled = this.enabled();
			addOrRemoveMainClasses(this.platformService, enabled, this.renderer, this.hostElement, this.className);
		});
	}
}

@Directive({
    selector: '[libLightText2]',
    standalone: false
})
export class TextLight2Directive implements ITextDirective {
	public readonly className = 'w-typography-light-text-2';

	public readonly enabled: Signal<boolean> = computed(() => {
		return this.libLightText2() === false ? false : true;
	});

	public readonly libLightText2 = input<boolean | ''>();

	constructor(
		private platformService: PlatformService,
		private renderer: Renderer2,
		private hostElement: ElementRef
	) {
		effect(() => {
			const enabled = this.enabled();
			addOrRemoveMainClasses(this.platformService, enabled, this.renderer, this.hostElement, this.className);
		});
	}
}
